import React, { useLayoutEffect, useRef, useState } from "react";
import {
    Box,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    CircularProgress,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { visuallyHidden } from '@mui/utils';
import { useSelector } from "react-redux";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        padding: "10px",
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: "10px",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:last-child td, &:last-child th": {
        border: "0 !important",
    },
}));

const TableComponent = (props) => {
    const {
        data,
        columns,
        isLoading,
        order,
        orderBy,
        createSortHandler,
        onRowClick,
        selectedRowId,
        boldRowIndexes = [],
        stickyRowIndexes = [],
        headerGroups,
    } = props;

    const stickyLeftByIndex = [];
    let runningLeft = 0;
    columns?.forEach((col, index) => {
        if (col.sticky) {
            stickyLeftByIndex[index] = runningLeft;
            // handle "250px" or number
            let w = 0;
            if (typeof col.width === "number") {
                w = col.width;
            } else if (typeof col.width === "string") {
                const parsed = parseInt(col.width, 10);
                w = isNaN(parsed) ? 150 : parsed;
            } else {
                w = 150; // default
            }
            runningLeft += w;
        }
    });

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    const setHeaderStyle = () => {
        if (headerRef.current) {
            setHeaderHeight(headerRef.current.offsetHeight);
        }
    }
    const isSidebarOpen = useSelector((state) => state.drawer.isDrawerOpen);

    useLayoutEffect(() => {
        setHeaderStyle();
        const timer = setTimeout(() => {
            setHeaderStyle();
        }, 250);

        return () => clearTimeout(timer);
    }, [isSidebarOpen]);

    return (
        <>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead ref={headerRef} sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 6,
                        backgroundColor: "#F9FAFB",
                    }}>
                        <TableRow>
                            {headerGroups?.map((group, index) => (
                                <StyledTableCell
                                    key={index}
                                    align="center"
                                    colSpan={group.colSpan}
                                    sx={{
                                        backgroundColor: "#F9FAFB",
                                        color: "#252525",
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        borderRight: "1px solid #E4E4E4",
                                    }}
                                >
                                    {group.label}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {columns?.map((col, index) =>
                                isLoading ? (
                                    <StyledTableCell key={`header-skeleton-${index}`}>
                                        <Skeleton variant="text" width={80} height={24} />
                                    </StyledTableCell>
                                ) :

                                    <StyledTableCell
                                        // sx={{
                                        //     whiteSpace: !col?.width && 'nowrap',
                                        //     color: "#252525",
                                        //     minWidth: col?.width || 'auto',
                                        //     borderTop: '1px solid rgba(224, 224, 224, 1)'
                                        // }}
                                        key={`${col?.name}-${index}`}
                                        sortDirection={orderBy === col.field ? order : false}
                                        sx={{
                                            whiteSpace: !col?.width && "nowrap",
                                            color: "#252525",
                                            minWidth: col?.width || "auto",
                                            borderTop: "1px solid rgba(224, 224, 224, 1)",
                                            backgroundColor: "#F9FAFB",
                                            zIndex: col.sticky ? 5 : 1,
                                            ...(col.sticky && {
                                                position: "sticky",
                                                left: stickyLeftByIndex[index] || 0,
                                            }),
                                        }}
                                    >
                                        {/* <TableSortLabel
                                            active={orderBy === col.field}
                                            direction={orderBy === col.field ? order : 'asc'}
                                            onClick={() => createSortHandler(col.field)}
                                        > */}
                                        {col.name}
                                        {/* {orderBy === col.field ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel> */}
                                    </StyledTableCell>

                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, i) => (
                                <StyledTableRow key={`skeleton-${i}`}>
                                    {columns?.map((_, j) => (
                                        <StyledTableCell key={j}>
                                            <Skeleton variant="text" width="100%" height={30} />
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            ))
                        ) : data?.length > 0 ? (
                            data?.map((row, index) => {
                                const isBold = boldRowIndexes.includes(index);
                                const isStickyRow = stickyRowIndexes.includes(index);

                                return (
                                    <StyledTableRow onClick={() => onRowClick ? onRowClick(row) : null} key={index}
                                        sx={{
                                            "&:hover .hoverButtons": { display: "flex" }, "&:hover .defaultIcon": { display: "none" },
                                            "&:not(:last-child) td": {
                                                // borderBottom: "1px solid #eee",
                                            },
                                            "&:last-child td": {
                                                borderBottom: "none",
                                            },
                                            bgcolor: row?.id === selectedRowId && "#F9FAFB",
                                            cursor: onRowClick && "pointer",
                                            "& th": { bgcolor: row.id === selectedRowId && "inherit" },
                                            fontWeight: isBold ? 600 : 400,

                                            //backgroundColor: isStickyRow ? "#FFFFFF" : "inherit",
                                            position: isStickyRow ? "sticky" : "static",
                                            top: isStickyRow ? `${headerHeight - 1}px` : "auto", // below header
                                            zIndex: isStickyRow ? 5 : 1,

                                            backgroundColor: isStickyRow ? "#007DD6" : isBold ? "#faebd7" : "inherit",
                                            //color: isStickyRow ? "#fff" : "inherit",
                                        }}>
                                        {columns?.map((col, ind) =>
                                            <StyledTableCell
                                                sx={{
                                                    whiteSpace: !col?.width && 'nowrap',
                                                    maxWidth: col?.width || 'auto',
                                                    color: "#474747",
                                                    // borderBottom: "1px solid #D1D1D1 !important",
                                                    fontWeight: isBold ? 600 : 400,
                                                    //backgroundColor: isStickyRow ? "#FFFFFF" : "#FFFFFF",
                                                    zIndex: col.sticky ? 5 : 1,
                                                    ...(col.sticky && {
                                                        position: "sticky",
                                                        left: stickyLeftByIndex[ind] || 0,
                                                    }),
                                                    color: isStickyRow ? "#fff" : "inherit",
                                                }}
                                                key={`${col.name}-${ind}`}
                                                component="th"
                                                scope="row"
                                            // sx={{
                                            //     whiteSpace: !col?.width && "nowrap",
                                            //     maxWidth: col?.width || "auto",
                                            //     color: "#474747",
                                            //     backgroundColor: "#FFFFFF",
                                            //     zIndex: col.sticky ? 2 : 1,
                                            //     ...(col.sticky && {
                                            //         position: "sticky",
                                            //         left: stickyLeftByIndex[ind] || 0,
                                            //     }),
                                            // }}
                                            >
                                                {col.renderer ? col.renderer(row, index) : row[col.field]}
                                            </StyledTableCell>
                                        )}
                                    </StyledTableRow>
                                )
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={columns?.length}>
                                    Record not found.
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default TableComponent;
